import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, FormControlLabel, IconButton, Link, ThemeProvider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setReport } from '../../source/slice/ReportSlice'
import ReportLines from './ReportLines'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import { LightTheme } from '@mercedes-benz/mui5-theme'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import BarChartIcon from '@mui/icons-material/BarChart'
import { AccessAlarm } from '@mui/icons-material'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import { useReactToPrint } from 'react-to-print'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import Statistics from './Statistics'
import OnCallShifts from './OnCallShifts'
import ReportPrintDetails from './ReportPrintDetails'
import LaunchIcon from '@mui/icons-material/Launch'
import ReportPrintDetailsOrder from './ReportPrintDetailsOrder'
import { IUser } from '../../interface/IUser'
import { IReport } from '../../interface/IReport'
import filterReportData from '../../utils/functions/filterReportData'
import { injectedEndpoints } from '@/api/InjectedEndpoints'

const Report = () => {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const contentRef = useRef<HTMLDivElement>(null)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const reportData: IReport[] = useSelector((state: any) => state.report?.value)
    const [open, setOpen] = useState(false)

    const [reportType, setReportType] = useState<string | null>('table')
    const [loading, setLoading] = useState<boolean>(false)
    const [expandAll, setExpandAll] = useState<boolean>(false)
    const [isPrinting, setIsPrinting] = useState<boolean>(false)
    const hasTimeCard = !!user?.worker?.mserp_usetimecard

    const [selectedMonth, setSelectedMonth] = useState<Date | null>(null)

    useEffect(() => {
        if (selectedMonth) {
            getSelectedDate(selectedMonth)
        }
    }, [selectedMonth, user?.worker?.mserp_personnelnumber])

    useEffect(() => {
        if (reportType === 'oncall') {
            const filtered = filterReportData(reportData)
            if (filtered.length !== reportData.length) {
                dispatch(setReport(filtered))
            }
        }
    }, [reportType, selectedMonth, reportData])

    const reactToPrintFn = useReactToPrint({
        contentRef,
        fonts: [{ family: 'MB Corpo S Text', source: '' }],
        documentTitle: `${selectedMonth ? format(selectedMonth, 'yyyy-MM') : ''}-Timetracking`,
        onBeforePrint: () =>
            new Promise<void>((resolve) => {
                setIsPrinting(true)
                requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
            }),
        onAfterPrint: () => setIsPrinting(false),
    })

    const getSelectedDate = async (e: any) => {
        setLoading(true)

        try {
            if (!e) return

            const date = format(e, 'yyyy-MM-01')

            let promise: ReturnType<typeof injectedEndpoints.endpoints.getReport.initiate> | ReturnType<typeof injectedEndpoints.endpoints.getReportFAK.initiate> | undefined

            if (hasTimeCard) {
                promise = (dispatch as any)(injectedEndpoints.endpoints.getReport.initiate(date, { subscribe: false, forceRefetch: false }))
            } else {
                promise = (dispatch as any)(injectedEndpoints.endpoints.getReportFAK.initiate(date, { subscribe: false, forceRefetch: false }))
            }

            const data = await (promise as any).unwrap()
            dispatch(setReport(data))
        } catch (err) {
            console.error('Failed to load report data', err)
        } finally {
            setLoading(false)
        }
    }

    const handleReportType = (event: React.MouseEvent<HTMLElement>, newReportType: string | null) => {
        if (newReportType !== null) setReportType(newReportType)
    }

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const printDocument = () => {
        reactToPrintFn()
    }

    return (
        <>
            <IconButton size="large" color="inherit" onClick={handleClickOpen}>
                <AssessmentOutlinedIcon />
            </IconButton>
            <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth={'xl'} scroll={'body'} sx={{ minWidth: 'lg' }}>
                {/* <DialogTitle id="alert-dialog-title">{'Monatsreport'}</DialogTitle> */}
                <Grid sx={{ display: 'flex', alignItems: 'center' }} container spacing={2}>
                    <Grid size={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', m: 2, ml: 4 }}>
                            <Typography variant="h4" component="h1" sx={{ mr: 2 }}>
                                {t('report.title')}
                            </Typography>
                            <Link href="https://corpdir.sharepoint.com/sites/mbti-apps-and-tools/SitePages/Reports.aspx" target="_blank">
                                <Chip label="HelpCenter" color="primary" variant="outlined" size="small" deleteIcon={<LaunchIcon />} onDelete={() => {}} />
                            </Link>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', m: 2, ml: 4 }}>
                            <Tooltip title={t('report.refresh')}>
                                <IconButton aria-label="refresh data" size="small" onClick={() => getSelectedDate(selectedMonth)} disabled={loading || !selectedMonth}>
                                    <AutorenewIcon />
                                </IconButton>
                            </Tooltip>
                            <ToggleButtonGroup size="small" value={reportType} onChange={handleReportType} exclusive unselectable="off">
                                <ToggleButton value={'table'} color={reportType === 'table' ? 'primary' : 'standard'}>
                                    <Tooltip title={t('report.table')}>
                                        <TableChartOutlinedIcon />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value={'chart'} color={reportType === 'chart' ? 'primary' : 'standard'}>
                                    <Tooltip title={t('report.chart')}>
                                        <BarChartIcon />
                                    </Tooltip>
                                </ToggleButton>
                                {user?.worker?.mserp_mbrecordoncallduty && (
                                    <ToggleButton value={'oncall'} color={reportType === 'oncall' ? 'primary' : 'standard'}>
                                        <Tooltip title={t('report.oncall')}>
                                            <AccessAlarm />
                                        </Tooltip>
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mr: 2, ml: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'de' ? de : enUS}>
                        <DatePicker
                            label={t('report.month')}
                            openTo="month"
                            views={['year', 'month']}
                            slotProps={{ textField: { size: 'small' } }}
                            onChange={(e: Date | null) => setSelectedMonth(e)}
                            defaultValue={selectedMonth ?? null}
                            minDate={new Date(2024, 12, 31)}
                            maxDate={new Date()}
                        />
                    </LocalizationProvider>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {reportType === 'table' ? (
                            <>
                                <FormControlLabel value="end" control={<Checkbox />} label={t('report.expandall')} labelPlacement="end" checked={expandAll} onChange={() => setExpandAll(!expandAll)} disabled={loading || !selectedMonth} />
                                <Tooltip title={t('report.printTooltip')} arrow>
                                    <Button variant="outlined" size="small" startIcon={<PrintOutlinedIcon />} onClick={() => printDocument()} disabled={loading || !selectedMonth}>
                                        {t('report.print')}
                                    </Button>
                                </Tooltip>
                            </>
                        ) : (
                            <></>
                        )}
                    </Box>
                </Box>
                <DialogContent ref={contentRef} sx={{ minWidth: '45vw' }}>
                    <ThemeProvider theme={isPrinting ? LightTheme : (outerTheme) => outerTheme}>
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress color="primary" />
                            </Box>
                        )}
                        {!loading && selectedMonth && (
                            <>
                                {reportType === 'table' && (
                                    <>
                                        <ReportPrintDetails month={selectedMonth} />
                                        <ReportLines expandAll={expandAll} hasTimeCard={hasTimeCard} />
                                        {(user?.worker?.mserp_employmentcategory === 'ANÜ' || user?.worker?.mserp_employmentcategory === 'FAK') && <ReportPrintDetailsOrder type={user?.worker?.mserp_employmentcategory} />}
                                    </>
                                )}
                                {reportType === 'chart' && <Statistics />}
                                {reportType === 'oncall' && <OnCallShifts date={selectedMonth} />}
                            </>
                        )}
                    </ThemeProvider>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default Report
